import React, { Component } from "react";
import axios from "axios";
import LineDiagram from "./graph/LineDiagram";
import { Utils } from "../Utils";
import MapD3 from "./graph/MapD3";

import {
  IONO_CONSTELLATIONS as CONSTELLATIONS,
  Constellation,
  CONS_SLUG,
  SAT_RANGES,
  satelliteName,
} from "../constellations";
import { noLiveReason } from "../ippStatus";

// Track history settings
const TRACK_HISTORY_LIMIT = 1800; // ~30 minutes at 1 pt/s
const TRACK_INITIAL_FETCH = 1800; // first load: 30 min of history
const TRACK_POLL_FETCH = 120;     // subsequent polls: 2 min of fresh data

interface Props { stationId: any; stationInfo: any; }

interface State {
  varioIono: any; internvalId: any; isStationOffline: boolean;
  currentSatellite: number; constellation: Constellation; mapIono: any;
  isPanelOpened: boolean; satelliteIntervalId: any; selectedSatelliteData: any;
  satelliteStatus: Record<number, boolean>; statusIntervalId: any;
  trackFlashKey: number;
}

class Iono extends Component<Props, State> {
  private trackHistory: Record<string, any[]> = {};
  private initialFetchDone = false;

  constructor(props: any) {
    super(props);
    this.state = {
      varioIono: null, mapIono: null, internvalId: null, isStationOffline: false,
      currentSatellite: 0, isPanelOpened: false, satelliteIntervalId: null,
      constellation: "GPS", selectedSatelliteData: null, satelliteStatus: {},
      statusIntervalId: null, trackFlashKey: 0,
    };
  }

  /** Merge new API data into accumulated history, dedup by timestamp, keep most recent TRACK_HISTORY_LIMIT */
  private accumulateTracks(newData: Record<string, any[]>): Record<string, any[]> {
    for (const sat of Object.keys(newData)) {
      const incoming = newData[sat] || [];
      const existing = this.trackHistory[sat] || [];
      // Merge: incoming is newest-first from API, existing is also newest-first
      const byTime = new Map<string, any>();
      for (const p of existing) byTime.set(p.t, p);
      for (const p of incoming) byTime.set(p.t, p);
      // Sort newest-first and trim
      const merged = Array.from(byTime.values())
        .sort((a, b) => (b.t > a.t ? 1 : b.t < a.t ? -1 : 0))
        .slice(0, TRACK_HISTORY_LIMIT);
      this.trackHistory[sat] = merged;
    }
    return { ...this.trackHistory };
  }

  getSatelliteName(n: number) {
    return satelliteName(this.state.constellation, n);
  }

  buildMarkers(data: any) {
    return Object.keys(data)
      .filter((id) => data[id]?.length > 0)
      .map((id) => ({
        id: parseInt(id),
        name: this.getSatelliteName(parseInt(id)),
        latLng: [data[id][0].lat, data[id][0].lon],
      }));
  }

  scrollToGraph = () => {
    document.getElementById("iono-graph")?.scrollIntoView({ behavior: "smooth" });
  };

  fetchSatelliteStatus() {
    axios
      .get(
        Utils.BACKEND_BASE_URL + "station/" + this.props.stationId.toLowerCase() +
        "/constellation/" + this.state.constellation + "/satellites/status"
      )
      .then((res) => { if (typeof res.data === "object") this.setState({ satelliteStatus: res.data }); })
      .catch(() => {});
  }

  mainMapData() {
    const fetchLimit = this.initialFetchDone ? TRACK_POLL_FETCH : TRACK_INITIAL_FETCH;
    axios
      .get(
        Utils.BACKEND_BASE_URL + "station/" + this.props.stationId.toLowerCase() +
        "/constellation/" + this.state.constellation + "/satellites?limit=" + fetchLimit
      )
      .then((res) => {
        if (typeof res.data == "object") {
          this.initialFetchDone = true;
          const accumulated = this.accumulateTracks(res.data);
          this.setState({ varioIono: accumulated, mapIono: this.buildMarkers(accumulated), isStationOffline: false });
        } else {
          this.setState({ varioIono: null, mapIono: null, isStationOffline: true });
        }
      })
      .catch(() => {
        this.setState({ isStationOffline: true, varioIono: null, mapIono: null });
      });
  }

  startPolling() {
    this.mainMapData();
    this.fetchSatelliteStatus();
    const int = window.setInterval(() => this.mainMapData(), 5000);
    const statusInt = window.setInterval(() => this.fetchSatelliteStatus(), 5000);
    this.setState({ internvalId: int, statusIntervalId: statusInt });
  }

  stopPolling() {
    window.clearInterval(this.state.internvalId);
    window.clearInterval(this.state.satelliteIntervalId);
    window.clearInterval(this.state.statusIntervalId);
  }

  componentDidMount() { this.startPolling(); }
  componentWillUnmount() { this.stopPolling(); }

  isSatelliteAlive(sat: number) { return this.state.satelliteStatus[sat] === true; }

  openSatelliteData(sat: number) {
    if (!this.isSatelliteAlive(sat)) return;
    window.clearInterval(this.state.satelliteIntervalId);
    this.setState({ currentSatellite: sat }, () => this.loadRealTimeData(sat));
  }

  /** Click on the time series: bring the map into view and blink the corresponding track */
  showSelectedTrack = () => {
    document.getElementById("iono-map")?.scrollIntoView({ behavior: "smooth", block: "center" });
    this.setState((st) => ({ trackFlashKey: st.trackFlashKey + 1 }));
  };

  /** Click on an IPP track on the map: the track exists, so its data does too (no "alive" check) */
  openSatelliteFromTrack(sat: number) {
    if (sat === this.state.currentSatellite) {
      this.scrollToGraph();
      return;
    }
    window.clearInterval(this.state.satelliteIntervalId);
    this.setState({ currentSatellite: sat, selectedSatelliteData: null }, () => this.loadRealTimeData(sat));
  }

  getRealTimeIonoData(satNumber: number, firstTime = false) {
    const slug = CONS_SLUG[this.state.constellation];
    axios
      .get(
        Utils.BACKEND_BASE_URL + "station/" + this.props.stationId.toLowerCase() +
        "/satellite/" + slug + "/number/" + satNumber
      )
      .then((res) => {
        if (typeof res.data == "object") {
          const data = [{
            id: this.state.constellation + "_" + this.state.currentSatellite,
            color: Utils.LINE_DIAGRAM_COLOR,
            data: res.data.slice().reverse().map((c: any) => ({ x: c.t, y: c.tec })),
          }];
          this.setState({ selectedSatelliteData: data }, () => {
            if (firstTime) this.scrollToGraph();
          });
        }
      })
      .catch(() => {});
  }

  loadRealTimeData(satNumber: number) {
    this.getRealTimeIonoData(satNumber, true);
    const int = window.setInterval(() => this.getRealTimeIonoData(satNumber), 1000);
    this.setState({ satelliteIntervalId: int });
  }

  handleConstellationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const constellation = e.target.value as Constellation;
    this.stopPolling();
    this.trackHistory = {};
    this.initialFetchDone = false;
    this.setState(
      { constellation, varioIono: null, mapIono: null, currentSatellite: 0, selectedSatelliteData: null, satelliteStatus: {}, isPanelOpened: false },
      () => this.startPolling()
    );
  };

  render() {
    const { constellation, mapIono, varioIono, isPanelOpened, currentSatellite, selectedSatelliteData, isStationOffline, satelliteStatus } = this.state;
    const satList = SAT_RANGES[constellation];

    const isLoading = !isStationOffline && !mapIono;

    return (
      <div className="space-y-4">
        {/* Loading state */}
        {isLoading && (
          <div className="rounded-xl bg-white shadow-sm border border-gray-100 flex flex-col items-center justify-center"
               style={{ minHeight: 420 }}>
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-brand-400" />
            <p className="mt-3 text-sm text-gray-500">Loading IPP tracks ({constellation})...</p>
          </div>
        )}

        {/* Map + panel */}
        {mapIono?.length > 0 && (
          <div id="iono-map" className="relative rounded-xl bg-white shadow-sm border border-gray-100 overflow-hidden"
               style={{ minHeight: 420 }}>
            <MapD3
              ionoData={varioIono}
              stationInfo={this.props.stationInfo}
              stationId={this.props.stationId}
              constellation={constellation}
              handleOpenMapPanel={() => this.setState({ isPanelOpened: !isPanelOpened })}
              onSatelliteClick={(sat) => this.openSatelliteFromTrack(sat)}
              selectedSatellite={currentSatellite}
              flashKey={this.state.trackFlashKey}
              liveStatus={satelliteStatus}
            />

            {/* Side panel */}
            <div className={`
              absolute top-0 right-0 h-full bg-white/95 backdrop-blur border-l border-gray-200
              overflow-y-auto transition-all duration-200
              ${isPanelOpened ? "w-52 md:w-56 p-3" : "w-0 p-0 overflow-hidden"}
            `}>
              {/* Constellation selector */}
              <div className="mb-3">
                <label className="block text-xs font-semibold text-brand-500 mb-1">
                  Constellation:
                </label>
                <select
                  value={constellation}
                  onChange={this.handleConstellationChange}
                  className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-700 focus:border-brand-300 focus:ring-1 focus:ring-brand-300 focus:outline-none"
                >
                  {CONSTELLATIONS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {/* Satellite list */}
              <ul className="space-y-0.5">
                {satList.map((sat) => {
                  const alive = this.isSatelliteAlive(sat);
                  return (
                    <li
                      key={sat}
                      title={alive ? "Live" : noLiveReason(varioIono?.[sat])}
                      onClick={() => this.openSatelliteData(sat)}
                      className={`
                        flex items-center justify-between rounded-md px-2 py-1.5 text-sm transition
                        ${alive
                          ? "cursor-pointer hover:bg-brand-50 text-gray-700"
                          : "cursor-default text-gray-300"
                        }
                        ${currentSatellite === sat ? "bg-brand-50 font-medium" : ""}
                      `}
                    >
                      {this.getSatelliteName(sat)}
                      <span
                        title={alive ? "Active" : "Inactive"}
                        className={`inline-block h-2.5 w-2.5 rounded-full ${
                          alive ? "bg-green-400" : "bg-red-300"
                        }`}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        {/* Satellite TEC graph */}
        <div id="iono-graph">
          {!isStationOffline && varioIono && currentSatellite > 0 && (
            <div
              className="rounded-xl bg-white shadow-sm border border-gray-100 overflow-hidden cursor-pointer"
              onClick={this.showSelectedTrack}
              title="Click to show the IPP track on the map"
            >
              <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
                <h6 className="text-sm font-semibold text-brand-500">
                  {this.getSatelliteName(currentSatellite)} — dsTEC/dt
                </h6>
                <span className="text-xs text-gray-400">Click to show track on map</span>
              </div>
              {satelliteStatus[currentSatellite] === false && (
                <div className="px-4 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-100">
                  No live data — {noLiveReason(varioIono?.[currentSatellite])}. The chart shows the last
                  available data.
                </div>
              )}
              <div className="p-2">
                <div className="chart-area">
                  {selectedSatelliteData && (
                    <LineDiagram data={selectedSatelliteData} legend="tecu/s" />
                  )}
                </div>
              </div>
            </div>
          )}
          {isStationOffline && (
            <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-6 text-center">
              <p className="text-sm text-gray-400 italic">
                Selected station is offline. Please try with another one.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default Iono;
